<template>
  <div
    class="pt-[120px] pb-[100px] flex items-center justify-center p-4 relative overflow-hidden"
    style="background: url('/login-bg.webp'); background-repeat: no-repeat; background-size: cover"
  >
    <div class="absolute inset-0"></div>

    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full animate-pulse"></div>
      <div class="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-300/30 rounded-full animate-ping"></div>
      <div class="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-purple-300/20 rounded-full animate-bounce"></div>
    </div>

    <div class="relative bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-white/20 mt-[100px]">
      <!-- En-tête -->
      <div class="text-center mb-8">
        <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-50 mb-4">
          <svg class="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 class="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
          {{ showSuccess ? 'Mot de passe modifié !' : 'Nouveau mot de passe' }}
        </h2>
        <p class="text-gray-500 text-sm leading-relaxed">
          {{ showSuccess ? 'Votre mot de passe a été modifié avec succès' : 'Choisissez un nouveau mot de passe sécurisé' }}
        </p>
      </div>

      <!-- Message de succès -->
      <div v-if="showSuccess" class="text-center py-4">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p class="text-gray-600 mb-6">
          Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
        </p>
        <button
          @click="handleRedirectToLogin"
          class="w-full bg-blue-600 cursor-pointer hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors font-semibold"
        >
          Se connecter
        </button>
      </div>

      <!-- Formulaire de réinitialisation -->
      <form v-else @submit.prevent="handleSubmit" class="space-y-4">
        <!-- Mot de passe -->
        <div>
          <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
            Nouveau mot de passe
          </label>
          <div class="relative">
            <input
              id="password"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm transition-all duration-200 pr-10"
              :class="localErrors.password ? 'border-red-500 focus:ring-red-500' : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-200'"
              placeholder="Entrez votre nouveau mot de passe"
              required
            />
            <button
              type="button"
              @click="togglePasswordVisibility"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="showPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path v-if="showPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                <path v-if="!showPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m9.02 9.02l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            </button>
          </div>
          <span v-if="localErrors.password" class="text-red-500 text-sm mt-1 block">
            {{ localErrors.password }}
          </span>
        </div>

        <!-- Confirmation du mot de passe -->
        <div>
          <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">
            Confirmer le mot de passe
          </label>
          <div class="relative">
            <input
              id="confirmPassword"
              v-model="form.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm transition-all duration-200 pr-10"
              :class="localErrors.confirmPassword ? 'border-red-500 focus:ring-red-500' : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-200'"
              placeholder="Confirmez votre nouveau mot de passe"
              required
            />
            <button
              type="button"
              @click="toggleConfirmPasswordVisibility"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="showConfirmPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path v-if="showConfirmPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                <path v-if="!showConfirmPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m9.02 9.02l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            </button>
          </div>
          <span v-if="localErrors.confirmPassword" class="text-red-500 text-sm mt-1 block">
            {{ localErrors.confirmPassword }}
          </span>
        </div>

        <!-- Indicateur de force du mot de passe -->
        <div v-if="form.password" class="space-y-2">
          <div class="flex justify-between text-xs text-gray-500">
            <span>Force du mot de passe</span>
            <span :class="passwordStrengthClass">{{ passwordStrengthText }}</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="h-2 rounded-full transition-all duration-300"
              :class="passwordStrengthBarClass"
              :style="{ width: passwordStrength + '%' }"
            ></div>
          </div>
        </div>

        <!-- Boutons -->
        <div class="flex space-x-3 pt-4">
          <button
            type="button"
            @click="handleCancel"
            class="flex-1 px-4 py-2 cursor-pointer border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
          >
            Annuler
          </button>
          <button
            type="submit"
            class="flex-1 bg-blue-600 cursor-pointer hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-60 font-semibold"
            :disabled="isLoading"
          >
            {{ isLoading ? 'Modification...' : 'Modifier' }}
          </button>
        </div>
      </form>

      <!-- Indicateur de sécurité -->
      <div class="mt-6 flex items-center justify-center gap-2 text-xs text-gray-400">
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
        </svg>
        <span>Connexion sécurisée SSL</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '../../composables/useToast'

const { success, error, warning, info, toast, clear } = useToast()
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const isLoading = ref(false);
const showSuccess = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const form = reactive({
  password: '',
  confirmPassword: ''
});

const localErrors = reactive({
  password: '',
  confirmPassword: ''
});

// Calcul de la force du mot de passe
const passwordStrength = computed(() => {
  const password = form.password;
  if (!password) return 0;

  let strength = 0;

  // Longueur minimale
  if (password.length >= 8) strength += 25;

  // Contient des minuscules
  if (/[a-z]/.test(password)) strength += 25;

  // Contient des majuscules
  if (/[A-Z]/.test(password)) strength += 25;

  // Contient des chiffres ou caractères spéciaux
  if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) strength += 25;

  return strength;
});

const passwordStrengthText = computed(() => {
  const strength = passwordStrength.value;
  if (strength === 0) return 'Faible';
  if (strength <= 50) return 'Moyen';
  if (strength <= 75) return 'Fort';
  return 'Très fort';
});

const passwordStrengthClass = computed(() => {
  const strength = passwordStrength.value;
  if (strength === 0) return 'text-gray-500';
  if (strength <= 50) return 'text-red-500';
  if (strength <= 75) return 'text-orange-500';
  return 'text-green-500';
});

const passwordStrengthBarClass = computed(() => {
  const strength = passwordStrength.value;
  if (strength === 0) return 'bg-gray-300';
  if (strength <= 50) return 'bg-red-500';
  if (strength <= 75) return 'bg-orange-500';
  return 'bg-green-500';
});

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value;
};

const validateForm = () => {
  localErrors.password = '';
  localErrors.confirmPassword = '';

  let isValid = true;

  // Validation du mot de passe
  if (!form.password.trim()) {
    localErrors.password = 'Le mot de passe est requis';
    isValid = false;
  } else if (form.password.length < 8) {
    localErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    isValid = false;
  } else if (!/(?=.*[a-z])(?=.*[A-Z])/.test(form.password)) {
    localErrors.password = 'Le mot de passe doit contenir des majuscules et minuscules';
    isValid = false;
  }

  // Validation de la confirmation
  if (!form.confirmPassword.trim()) {
    localErrors.confirmPassword = 'La confirmation du mot de passe est requise';
    isValid = false;
  } else if (form.password !== form.confirmPassword) {
    localErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  isLoading.value = true;

  const token = route.params.token;
  if (!token) {
    error('Token de réinitialisation manquant');
    isLoading.value = false;
    return;
  }

  const result = await authStore.confirmPasswordReset(route.params.first+"/"+token, form.password);

  if (result.success) {
    showSuccess.value = true;
    success('Mot de passe modifié avec succès !');
  } else {
    error(result.error || 'Erreur lors de la modification du mot de passe');
  }

  isLoading.value = false;
};

const handleCancel = () => {
  router.push('/login');
};

const handleRedirectToLogin = () => {
  router.push('/login');
};

onMounted(() => {
  // Vérifier si le token est présent dans l'URL
  const token = route.params.token;
  if (!token) {
    error('Lien de réinitialisation invalide');
    router.push('/login');
  }
});
</script>

<style scoped>
</style>
